import { useEffect, useState } from "react";
import { Eye, Pencil, RefreshCcw } from "lucide-react";
import { useNavigate } from "react-router-dom";
import "../../styles/settings/UserManagement.css";

const API_BASE = process.env.REACT_APP_API_BASE_URL;

const UserManagement = () => {
  const navigate = useNavigate();

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("All");
  const [statusFilter, setStatusFilter] = useState("All");

  const [viewUser, setViewUser] = useState(null);
  const [editingUser, setEditingUser] = useState(null);
  const [resetUser, setResetUser] = useState(null);

  const [passwords, setPasswords] = useState({
    newPassword: "",
    confirmPassword: "",
  });

  /* ================= FETCH USERS ================= */
  const fetchUsers = async () => {
    try {
      const res = await fetch(`${API_BASE}/admin/users`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });
      const data = await res.json();
      setUsers(data);
    } catch (err) {
      console.error("Fetch users failed", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  /* ================= TOGGLE USER STATUS ================= */
  const handleToggleStatus = async (user) => {
    const newStatus = user.user_status === 1 ? 0 : 1;

    // Optimistic UI update
    setUsers((prev) =>
      prev.map((u) =>
        u.user_id === user.user_id
          ? { ...u, user_status: newStatus }
          : u
      )
    );

    try {
      const res = await fetch(
        `${API_BASE}/admin/users/${user.user_id}/status`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
          body: JSON.stringify({ user_status: newStatus }),
        }
      );

      if (!res.ok) throw new Error("Status update failed");
    } catch (err) {
      alert("Failed to update status");

      // rollback
      setUsers((prev) =>
        prev.map((u) =>
          u.user_id === user.user_id
            ? { ...u, user_status: user.user_status }
            : u
        )
      );
    }
  };

  /* ================= EDIT USER ================= */
  const handleEditUserSave = async () => {
    try {
      const res = await fetch(
        `${API_BASE}/admin/users/${editingUser.user_id}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
          body: JSON.stringify({
            user_name: editingUser.user_name,
            contact_number: editingUser.contact_number,
            role: editingUser.role,
          }),
        }
      );

      if (!res.ok) throw new Error("Failed to update user");

      setEditingUser(null);
      fetchUsers();
    } catch (err) {
      alert(err.message);
    }
  };

  /* ================= RESET PASSWORD ================= */
  const handleResetPassword = async () => {
    if (!passwords.newPassword || !passwords.confirmPassword) {
      alert("Please fill both fields");
      return;
    }

    if (passwords.newPassword !== passwords.confirmPassword) {
      alert("Passwords do not match");
      return;
    }

    try {
      const res = await fetch(
        `${API_BASE}/admin/users/${resetUser.user_id}/reset-password`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
          body: JSON.stringify({
            password: passwords.newPassword,
          }),
        }
      );

      if (!res.ok) throw new Error("Failed to reset password");

      alert("Password updated successfully");
      setResetUser(null);
      setPasswords({ newPassword: "", confirmPassword: "" });
    } catch (err) {
      alert(err.message);
    }
  };

  /* ================= FILTER ================= */
  const filteredUsers = users.filter((u) => {
    const matchesSearch =
      u.user_name.toLowerCase().includes(search.toLowerCase()) ||
      u.email.toLowerCase().includes(search.toLowerCase());

    const matchesRole = roleFilter === "All" || u.role === roleFilter;

    const matchesStatus =
      statusFilter === "All" ||
      (statusFilter === "Active" && u.user_status === 1) ||
      (statusFilter === "Inactive" && u.user_status === 0);

    return matchesSearch && matchesRole && matchesStatus;
  });

  if (loading) return <p>Loading users...</p>;

  return (
    <div className="settings-page">
      {/* HEADER */}
      <div className="settings-header">
        <h2>User Management</h2>
        <button
          className="add-btn"
          onClick={() => navigate("/admin/settings/add-user")}
        >
          + Add User
        </button>
      </div>

      {/* FILTERS */}
      <div className="filters">
        <input
          placeholder="Search by name or email"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />

        <select value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)}>
          <option value="All">All Roles</option>
          <option value="Admin">Admin</option>
          <option value="User">User</option>
        </select>

        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
        >
          <option value="All">All Status</option>
          <option value="Active">Active</option>
          <option value="Inactive">Inactive</option>
        </select>
      </div>

      {/* TABLE */}
      <table className="settings-table">
        <thead>
          <tr>
            <th>User ID</th>
            <th>Name & Email</th>
            <th>Contact</th>
            <th>Role</th>
            <th>Created</th>
            <th>Status</th>
            <th>Action</th>
          </tr>
        </thead>

        <tbody>
          {filteredUsers.map((u) => (
            <tr key={u.user_id}>
              <td>{u.user_id}</td>
              <td>
                <strong>{u.user_name}</strong>
                <br />
                <span className="muted">{u.email}</span>
              </td>
              <td>{u.contact_number || "-"}</td>
              <td>{u.role}</td>
              <td>{new Date(u.createdAt).toLocaleDateString()}</td>

              {/* ✅ STATUS TOGGLE */}
              <td>
                <label className="switch">
                  <input
                    type="checkbox"
                    checked={u.user_status === 1}
                    onChange={() => handleToggleStatus(u)}
                  />
                  <span className="slider"></span>
                </label>
              </td>

              <td>
                <button className="action-btn" onClick={() => setViewUser(u)}>
                  <Eye size={16} />
                </button>
                <button className="action-btn" onClick={() => setEditingUser(u)}>
                  <Pencil size={16} />
                </button>
                <button className="action-btn" onClick={() => setResetUser(u)}>
                  <RefreshCcw size={16} />
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* ================= VIEW USER ================= */}
      {viewUser && (
        <div className="modal-overlay">
          <div className="modal-card user-view-modal">
            <h2>User Details</h2>

            <div className="user-info-grid">
              <div className="label">Name</div>
              <div className="value">{viewUser.user_name}</div>

              <div className="label">Email</div>
              <div className="value">{viewUser.email}</div>

              <div className="label">Contact</div>
              <div className="value">{viewUser.contact_number || "-"}</div>

              <div className="label">Role</div>
              <div className="value">{viewUser.role}</div>

              <div className="label">Status</div>
              <div
                className={`value status ${
                  viewUser.user_status === 1 ? "active" : "inactive"
                }`}
              >
                {viewUser.user_status === 1 ? "Active" : "Inactive"}
              </div>
            </div>

            <div className="modal-actions center">
              <button
                className="secondary-btn"
                onClick={() => setViewUser(null)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ================= EDIT USER ================= */}
      {editingUser && (
        <div className="modal-overlay">
          <div className="modal-card">
            <h2>Edit User</h2>

            <label>Name</label>
            <input
              value={editingUser.user_name}
              onChange={(e) =>
                setEditingUser({ ...editingUser, user_name: e.target.value })
              }
            />

            <label>Email</label>
            <input value={editingUser.email} disabled className="readonly" />

            <label>Contact</label>
            <input
              value={editingUser.contact_number}
              onChange={(e) =>
                setEditingUser({
                  ...editingUser,
                  contact_number: e.target.value,
                })
              }
            />

            <label>Role</label>
            <select
              value={editingUser.role}
              onChange={(e) =>
                setEditingUser({ ...editingUser, role: e.target.value })
              }
            >
              <option>Admin</option>
              <option>User</option>
            </select>

            <div className="modal-actions">
              <button
                className="secondary-btn"
                onClick={() => setEditingUser(null)}
              >
                Cancel
              </button>
              <button className="primary-btn" onClick={handleEditUserSave}>
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ================= RESET PASSWORD ================= */}
      {resetUser && (
        <div className="modal-overlay">
          <div className="modal-card">
            <h2>Reset Password</h2>

            <input
              type="password"
              placeholder="New Password"
              value={passwords.newPassword}
              onChange={(e) =>
                setPasswords({ ...passwords, newPassword: e.target.value })
              }
            />

            <input
              type="password"
              placeholder="Confirm Password"
              value={passwords.confirmPassword}
              onChange={(e) =>
                setPasswords({ ...passwords, confirmPassword: e.target.value })
              }
            />

            <div className="modal-actions">
              <button
                className="secondary-btn"
                onClick={() => {
                  setResetUser(null);
                  setPasswords({ newPassword: "", confirmPassword: "" });
                }}
              >
                Cancel
              </button>
              <button className="primary-btn" onClick={handleResetPassword}>
                Update
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagement;
